// Histórico de saídas e voltas dos kits (06/10/2026) — lógica pura da tela
// (o jest do CRA não carrega o antd v6; aqui dá para testar de verdade).
//
// Pedido do Lucas: "visualizar o histórico de entradas e saídas de cada kit —
// como está, não consigo rastrear se há algum problema". O servidor
// (op_kit_historico) devolve os check-ins do período; kit, contrato e pessoa
// são filtrados aqui, na hora, para as listas dos filtros não encolherem.

export type RegistroKit = {
  id: string;
  kit_id: string; kit_nome: string; kit_codigo: string | null;
  contrato_id: string | null; contrato_numero: string | null; contrato_titulo: string | null;
  /** contrato principal (o aditivo sobe ao pai, como no custo do equipamento) */
  contrato_raiz_id: string | null; contrato_raiz_numero: string | null; contrato_raiz_titulo: string | null;
  contrato_da_campanha: boolean; campanha_id: string | null; campanha_titulo: string | null;
  usuario_email: string; usuario_nome: string;
  saiu_em: string; voltou_em: string | null;
  devolvido_por: string | null; devolvido_por_nome: string | null;
  observacao: string | null; observacao_saida: string | null;
  aberto: boolean; dias_em_campo: number; itens_na_saida: number;
};

/** Kit fora há mais que isto vira destaque — o mesmo prazo do aviso do resultado por contrato. */
export const DIAS_ALERTA_ABERTO = 30;

/** `dias_em_campo` conta o dia da saída E o de hoje; o aviso do motor conta os dias
 *  decorridos (hoje − saída). Fora "há mais de 30 dias" = mais de 31 dias contados. */
export const emDestaque = (r: Pick<RegistroKit, 'aberto' | 'dias_em_campo'>): boolean =>
  r.aberto && r.dias_em_campo - 1 > DIAS_ALERTA_ABERTO;

export const textoDias = (n: number): string => (n === 1 ? '1 dia' : `${n} dias`);

/** Data e hora em São Paulo, com o dia da semana: "dom 06/09/2026 16:56". */
export function dataHoraSP(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = Object.fromEntries(new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo', weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d).map(x => [x.type, x.value]));
  const dia = String(p.weekday || '').replace('.', '').toLowerCase();
  return `${dia} ${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`;
}

const semAcento = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const porRotulo = (a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label, 'pt-BR');

/** Opções dos filtros a partir do que veio (sem repetir, em ordem alfabética). */
export function opcoesDosFiltros(regs: readonly RegistroKit[]) {
  const kits = new Map<string, string>(), contratos = new Map<string, string>(), pessoas = new Map<string, string>();
  for (const r of regs) {
    kits.set(r.kit_id, r.kit_codigo ? `${r.kit_nome} (${r.kit_codigo})` : r.kit_nome);
    // o filtro é pelo contrato principal: escolher o contrato traz também os aditivos
    if (r.contrato_raiz_id) contratos.set(r.contrato_raiz_id, [r.contrato_raiz_numero, r.contrato_raiz_titulo].filter(Boolean).join(' — '));
    pessoas.set(r.usuario_email.toLowerCase(), r.usuario_nome || r.usuario_email);
    if (r.devolvido_por && !pessoas.has(r.devolvido_por.toLowerCase())) pessoas.set(r.devolvido_por.toLowerCase(), r.devolvido_por_nome || r.devolvido_por);
  }
  const lista = (m: Map<string, string>) => Array.from(m, ([value, label]) => ({ value, label })).sort(porRotulo);
  return { kits: lista(kits), contratos: lista(contratos), pessoas: lista(pessoas) };
}

export type FiltroKits = { kit?: string | null; contrato?: string | null; pessoa?: string | null; texto?: string };

/** Pessoa = quem levou OU quem devolveu. Texto procura em kit, contrato, campanha, pessoas e observações. */
export function filtrar(regs: readonly RegistroKit[], f: FiltroKits): RegistroKit[] {
  const q = semAcento(String(f.texto || '').trim());
  const pessoa = f.pessoa ? f.pessoa.toLowerCase() : null;
  return regs.filter(r => {
    if (f.kit && r.kit_id !== f.kit) return false;
    if (f.contrato && r.contrato_raiz_id !== f.contrato) return false;
    if (pessoa && r.usuario_email.toLowerCase() !== pessoa && String(r.devolvido_por || '').toLowerCase() !== pessoa) return false;
    if (!q) return true;
    const alvo = [r.kit_nome, r.kit_codigo, r.contrato_numero, r.contrato_titulo, r.campanha_titulo, r.usuario_nome,
      r.devolvido_por_nome, r.observacao, r.observacao_saida].filter(Boolean).join(' ');
    return semAcento(alvo).includes(q);
  });
}

/** Resumo de uma linha para o topo da lista. */
export function resumoDoHistorico(regs: readonly RegistroKit[]): { saidas: number; emCampo: number; destaque: number; diasEmCampo: number } {
  return {
    saidas: regs.length,
    emCampo: regs.filter(r => r.aberto).length,
    destaque: regs.filter(emDestaque).length,
    diasEmCampo: regs.reduce((a, r) => a + (Number(r.dias_em_campo) || 0), 0),
  };
}
